import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import startOfDay from 'date-fns/startOfDay'
import { usePageTitle } from '../../../actions/app'
import * as VariantActions from '../../../actions/variants'
import { useAppDispatch } from '../../../actions/types'
import { Button, Centered, Column, Confirmation, Loader, Padding, SearchInput, Table } from '../../../components'
import { colors, Routes } from '../../../constants'
import { useAppSelector } from '../../../reducers'
import * as VariantSelectors from '../../../reducers/variants'
import type { Variant } from '../../../types'
import { ArrayUtils } from '../../../utils'
import VariantForm from './VariantForm/VariantForm'
import variantTableDisplay from './Variants.table'
import useVariantParams from './useVariantParams'
import { useSize } from '../../../components/utils'

export type ExtendedVariant = Variant & { selected: boolean }

export default function Variants() {
    const dispatch = useAppDispatch()
    const isWorking = useAppSelector(VariantSelectors.isWorking)
    const isLoadingSelectedVariant = useAppSelector(VariantSelectors.isLoadingSelectedVariant)
    const variants = useAppSelector(VariantSelectors.selectVariants)
    const selectedVariant = useAppSelector(VariantSelectors.selectedVariant)
    const { t } = useTranslation()
    const history = useHistory()
    const { isMobile } = useSize()
    const { estate, variantId } = useVariantParams()

    usePageTitle(variantId && selectedVariant ? t('variant-title') : t('variants-title'))

    const formRef = React.useRef<HTMLDivElement>(null)

    const [bulkSelectedVariants, setBulkSelectedVariants] = React.useState<number[]>([])
    const [isCreatingNewVariant, setIsCreatingNewVariant] = React.useState(false)
    const [searchTerm, setSearchTerm] = React.useState('')
    const today = React.useMemo(() => startOfDay(new Date()), [])

    React.useEffect(() => {
        dispatch(VariantActions.fetchVariants())
    }, [dispatch, estate])

    React.useEffect(() => {
        if (variantId) {
            setIsCreatingNewVariant(false)
            dispatch(VariantActions.fetchVariant(variantId))

            if (isMobile) {
                formRef.current?.scrollIntoView()
            }
        } else if (isMobile) {
            window.scrollTo(0, 0)
        }
    }, [dispatch, estate, variantId, isMobile])

    const filteredVariants = React.useMemo(() => {
        const normalizedTerm = searchTerm.trim().toLowerCase()
        const sortByDateStartAsc = (list: Variant[]) =>
            [...list].sort((a, b) => a.start_date.getTime() - b.start_date.getTime())

        if (normalizedTerm.length >= 3) {
            return sortByDateStartAsc(variants.filter(variant => {
                const matchesSku = variant.skus.some(sku => sku.toLowerCase().includes(normalizedTerm))
                return matchesSku
            }))
        }

        return sortByDateStartAsc(variants.filter(variant => {
            const start = startOfDay(variant.start_date)
            const end = startOfDay(variant.end_date)
            return (start <= today && end >= today) || start > today
        }))
    }, [variants, searchTerm, today])

    const extendedVariants: ExtendedVariant[] = React.useMemo(() => {
        return filteredVariants.map(variant => {
            return {
                ...variant,
                selected: bulkSelectedVariants.includes(variant.id),
            }
        })
    }, [filteredVariants, bulkSelectedVariants])

    if (isWorking) {
        return (
            <Padding top>
                <Centered horizontal>
                    <Loader />
                </Centered>
            </Padding>
        )
    }

    const onClickRow = (clickedVariantId?: number) => {
        if (!clickedVariantId) {
            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_VARIANTS, { estate }),
            )
            return
        }

        const path = Routes.buildRoute(
            Routes.SELF_SERVE_VARIANTS,
            { estate, variant: clickedVariantId },
        )

        if (isMobile) {
            history.push(path)
        } else {
            history.replace(path)
        }
    }

    const onSelectVariant = (id: number, isSelected: boolean) => {
        setBulkSelectedVariants(prevSelection => {
            const itemIndex = prevSelection.findIndex(selectedId => selectedId === id)
            const isAlreadyIncluded = itemIndex > -1

            if (isSelected && !isAlreadyIncluded) {
                return [ ...prevSelection, id ]
            } else if (!isSelected && isAlreadyIncluded) {
                return ArrayUtils.removeItem(prevSelection, itemIndex)
            }

            return prevSelection
        })
    }

    const onDeleteVariants = () => {
        const fn = async () => {
            const isDeletingSelected = !!variantId && bulkSelectedVariants.includes(variantId)

            await Promise.all(bulkSelectedVariants.map(async id => {
                await dispatch(VariantActions.deleteVariant(id))
            }))

            if (isDeletingSelected) {
                history.replace(
                    Routes.buildRoute(Routes.SELF_SERVE_VARIANTS, { estate })
                )
                dispatch(VariantActions.fetchVariants())
            } else {
                dispatch(VariantActions.fetchVariants())
            }

            setBulkSelectedVariants([])
        }

        fn()
    }

    const onClickCreate = () => {
        setIsCreatingNewVariant(true)
        onClickRow()

        if (isMobile) {
            formRef.current?.scrollIntoView()
        }
    }

    return (
        <>
            <Padding>
                <Button
                    theme="forestGreen"
                    onClick={onClickCreate}
                >
                    {t('variant-create')}
                </Button>
                <Padding top>
                    <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', marginBottom: '10px' }}>
                        <SearchInput
                            width="280px"
                            value={searchTerm}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                        />
                        {searchTerm.length > 0 && (
                            <button
                                aria-label="Clear search"
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    color: colors.greydark,
                                    padding: 0,
                                    height: '16px',
                                    width: '16px',
                                    lineHeight: '16px',
                                }}
                            >
                                x
                            </button>
                        )}
                    </div>
                </Padding>
                <Confirmation
                    cancel={t('cancel')}
                    confirmation={t('variant-remove-confirm-body', {
                        count: bulkSelectedVariants.length,
                    })}
                    header={t('variant-remove-confirm-header')}
                    onCancel={() => null}
                    onConfirm={onDeleteVariants}
                    ok={t('variant-remove-confirm-button')}
                    theme="danger"
                >
                    <Button
                        theme={bulkSelectedVariants.length > 0 ? 'danger' : 'disabled'}
                        disabled={bulkSelectedVariants.length === 0}
                    >
                        {bulkSelectedVariants.length === 0 && t('variant-remove-count-none')}
                        {bulkSelectedVariants.length > 0 && t('variant-remove-count', { count: bulkSelectedVariants.length })}
                    </Button>
                </Confirmation>
            </Padding>
            <Column width={isMobile ? '100%' : '50%'}>
                <Table
                    data={extendedVariants}
                    display={
                        variantTableDisplay({ onSelectVariant, t })
                    }
                    keyField="id"
                    onClickRow={onClickRow}
                    hideDeviceButtons
                    selectedItem={variantId}
                />
            </Column>
            <Column
                ref={formRef}
                width={isMobile ? '100%' : '50%'}
                color={colors.bluedarkest}
            >
                {!selectedVariant && isLoadingSelectedVariant && (
                    <Padding top>
                        <Centered horizontal>
                            <Loader />
                        </Centered>
                    </Padding>
                )}
                {(isCreatingNewVariant || !selectedVariant) && <VariantForm />}
                {!isCreatingNewVariant && selectedVariant && <VariantForm variant={selectedVariant} />}
            </Column>
        </>
    )
}
